/**
 * Statuses that require a comment (SD-145) — client plumbing.
 *
 * Two layers keep every web status change covered:
 *
 * 1. Surfaces that know the target status (pickers, board drops, bulk bars)
 *    ask up front with `confirmStatusComment` (via useStatusChangeWithComment)
 *    and pass the comment to the API, so cancel means nothing moved.
 * 2. `withStatusCommentRetry` wraps every task status write in the API layer.
 *    When the server still answers STATUS_COMMENT_REQUIRED (a surface that
 *    could not see the flag, a workflow that differs per task), it prompts
 *    and retries once with the comment. Cancel rejects with
 *    StatusChangeCanceledError, which callers treat as a revert, not a failure.
 */

import { requestStatusComment } from '@/store/status-comment-prompt.store'

import {
  normalizeStatusComment,
  readStatusCommentRequiredError,
  statusRequiresComment,
} from '@scrambl/shared'

/** Thrown when the person dismisses the comment prompt: nothing was saved. */
export class StatusChangeCanceledError extends Error {
  readonly isStatusChangeCanceled = true

  constructor() {
    super('Status change canceled')
    this.name = 'StatusChangeCanceledError'
  }
}

export const isStatusChangeCanceled = (error: unknown): boolean =>
  error instanceof StatusChangeCanceledError ||
  (!!error &&
    typeof error === 'object' &&
    (error as { isStatusChangeCanceled?: unknown }).isStatusChangeCanceled ===
      true)

/** The subset of a status definition the prompt needs. */
export interface StatusCommentTarget {
  name?: string | null
  color?: string | null
  requiresComment?: boolean | null
}

export type StatusCommentDecision =
  | { confirmed: true; statusComment?: string }
  | { confirmed: false }

/**
 * Ask for a comment when `status` requires one. Resolves `{ confirmed:
 * false }` when the person cancels; otherwise `{ confirmed: true }` with the
 * comment (absent when the status does not require one).
 */
export async function confirmStatusComment(
  status: StatusCommentTarget | null | undefined,
  options: { taskCount?: number } = {},
): Promise<StatusCommentDecision> {
  if (!statusRequiresComment(status)) return { confirmed: true }
  const comment = await requestStatusComment({
    statusName: status?.name?.trim() || 'this status',
    statusColor: status?.color ?? null,
    taskCount: Math.max(1, options.taskCount ?? 1),
  })
  return comment
    ? { confirmed: true, statusComment: comment }
    : { confirmed: false }
}

/**
 * One prompt for a multi-task move that may land tasks in different statuses
 * (e.g. "mark all as done" across workflows): asks once when any target
 * requires a comment, and the same comment goes to every moved task.
 */
export async function confirmStatusCommentForMany(
  targets: (StatusCommentTarget | null | undefined)[],
  taskCount: number,
): Promise<StatusCommentDecision> {
  const requiring = targets.find(target => statusRequiresComment(target))
  if (!requiring) return { confirmed: true }
  return confirmStatusComment(requiring, { taskCount })
}

/**
 * Run a status write; if the server says the target status requires a
 * comment and none was sent, prompt once and resend with it. `send` receives
 * the comment to include (undefined on the first attempt unless the caller
 * already has one).
 */
export async function withStatusCommentRetry<T>(
  send: (statusComment: string | undefined) => Promise<T>,
  options: { statusComment?: string | null; taskCount?: number } = {},
): Promise<T> {
  const initial = normalizeStatusComment(options.statusComment) ?? undefined
  try {
    return await send(initial)
  } catch (error) {
    const required = readStatusCommentRequiredError(error)
    if (!required || initial) throw error
    const comment = await requestStatusComment({
      statusName: required.statusName || 'this status',
      taskCount: Math.max(1, options.taskCount ?? 1),
    })
    if (!comment) throw new StatusChangeCanceledError()
    return send(comment)
  }
}
